// themes.js - Comic book style themes for Ribbon Rider Comics

export const THEMES = {
  noir: {
    id: 'noir',
    name: 'Noir',
    subtitle: 'Dark & Mysterious',
    description: 'High contrast shadows, dramatic lighting, and a moody atmosphere',
    preview: {
      primary: '#e94560',
      secondary: '#1a1a2e',
      accent: '#4cc9f0'
    },
    colors: {
      // Base colors
      black: '#0a0a0f',
      darker: '#0f0f18',
      dark: '#1a1a2e',
      mid: '#2d2d44',
      light: '#4a4a6a',
      // Accent colors
      primary: '#e94560',
      primaryDark: '#c73e54',
      primaryGlow: 'rgba(233, 69, 96, 0.4)',
      secondary: '#0f3460',
      secondaryLight: '#16213e',
      accentGold: '#f4a261',
      accentBlue: '#4cc9f0',
      accentGreen: '#2ec4b6',
      accentPurple: '#7b2cbf',
      // Text colors
      text: '#eaeaea',
      textDim: '#a0a0a0',
      textBright: '#ffffff',
    },
    fonts: {
      display: "'Bangers', cursive",
      comic: "'Comic Neue', cursive",
      typewriter: "'Special Elite', cursive",
      marker: "'Permanent Marker', cursive",
      tech: "'Orbitron', sans-serif",
      body: "'Montserrat Alternates', sans-serif",
    },
    effects: {
      overlay: 'halftone',
      vignette: true,
      grain: true,
      contrast: 1.1,
      saturate: 0.8,
    },
    imagePrompt: {
      style: 'noir comic book style, high contrast black and white with {accentColor} accent, dramatic shadows, halftone dots, cinematic lighting',
      negativePrompt: 'colorful, bright, cheerful, cartoon',
      accentColors: {
        danger: 'red',
        mystery: 'blue',
        hope: 'golden amber',
        tension: 'green',
        power: 'purple'
      }
    }
  },

  popArt: {
    id: 'popArt',
    name: 'Pop Art',
    subtitle: 'Bold & Vibrant',
    description: 'Bright colors, Ben-Day dots, and classic 60s comic energy',
    preview: {
      primary: '#FFD700',
      secondary: '#2196F3',
      accent: '#FF1744'
    },
    colors: {
      // Base colors - dark backgrounds for readability
      black: '#0a0a12',
      darker: '#12121f',
      dark: '#1a1a2e',
      mid: '#2d2d4a',
      light: '#4a4a70',
      // Accent colors - vibrant Pop Art palette
      primary: '#FFD700',
      primaryDark: '#E6C200',
      primaryGlow: 'rgba(255, 215, 0, 0.4)',
      secondary: '#2196F3',
      secondaryLight: '#64B5F6',
      accentGold: '#FF9800',
      accentBlue: '#2196F3',
      accentGreen: '#4CAF50',
      accentPurple: '#9C27B0',
      // Text colors - bright for contrast
      text: '#f0f0f0',
      textDim: '#b0b0b0',
      textBright: '#ffffff',
    },
    fonts: {
      display: "'Bangers', cursive",
      comic: "'Comic Neue', cursive",
      typewriter: "'Special Elite', cursive",
      marker: "'Permanent Marker', cursive",
      tech: "'Orbitron', sans-serif",
      body: "'Comic Neue', cursive",
    },
    effects: {
      overlay: 'benday',
      vignette: false,
      grain: false,
      contrast: 1.2,
      saturate: 1.4,
    },
    imagePrompt: {
      style: 'pop art comic book style, bold primary colors red yellow blue, large Ben-Day dots pattern, thick black outlines, 1960s Roy Lichtenstein aesthetic, vibrant and bold',
      negativePrompt: 'dark, muted, noir, gritty, realistic',
      accentColors: {
        danger: 'red',
        mystery: 'blue',
        hope: 'yellow',
        tension: 'orange',
        power: 'magenta'
      }
    }
  },

  manga: {
    id: 'manga',
    name: 'Manga',
    subtitle: 'Dynamic & Expressive',
    description: 'Clean lines, dramatic speed lines, and Japanese comic aesthetics',
    preview: {
      primary: '#000000',
      secondary: '#ffffff',
      accent: '#ff6b9d'
    },
    colors: {
      // Base colors
      black: '#0d0d0d',
      darker: '#1a1a1a',
      dark: '#2d2d2d',
      mid: '#4a4a4a',
      light: '#808080',
      // Accent colors
      primary: '#ff6b9d',
      primaryDark: '#e55a87',
      primaryGlow: 'rgba(255, 107, 157, 0.4)',
      secondary: '#1a1a1a',
      secondaryLight: '#333333',
      accentGold: '#ffd93d',
      accentBlue: '#6bcfff',
      accentGreen: '#95e99b',
      accentPurple: '#c77dff',
      // Text colors
      text: '#f0f0f0',
      textDim: '#b0b0b0',
      textBright: '#ffffff',
    },
    fonts: {
      display: "'Bangers', cursive",
      comic: "'Comic Neue', cursive",
      typewriter: "'Special Elite', cursive",
      marker: "'Permanent Marker', cursive",
      tech: "'Orbitron', sans-serif",
      body: "'Montserrat Alternates', sans-serif",
    },
    effects: {
      overlay: 'screentone',
      vignette: false,
      grain: false,
      contrast: 1.3,
      saturate: 0.1,
    },
    imagePrompt: {
      style: 'manga comic style, black and white with screentone shading, dramatic speed lines, clean precise linework, Japanese comic aesthetic, dynamic composition, high contrast',
      negativePrompt: 'colorful, western comic, realistic, photograph',
      accentColors: {
        danger: 'black',
        mystery: 'gray',
        hope: 'white',
        tension: 'dark gray',
        power: 'black'
      }
    }
  },

  vintage: {
    id: 'vintage',
    name: 'Golden Age',
    subtitle: 'Classic & Nostalgic',
    description: 'Warm sepia tones, aged paper texture, and 1940s charm',
    preview: {
      primary: '#c9a227',
      secondary: '#8b4513',
      accent: '#2f5233'
    },
    colors: {
      // Base colors
      black: '#1a1510',
      darker: '#2a2318',
      dark: '#3d3425',
      mid: '#5c5040',
      light: '#8a7d68',
      // Accent colors
      primary: '#c9a227',
      primaryDark: '#a68521',
      primaryGlow: 'rgba(201, 162, 39, 0.4)',
      secondary: '#8b4513',
      secondaryLight: '#a0522d',
      accentGold: '#daa520',
      accentBlue: '#4a6670',
      accentGreen: '#2f5233',
      accentPurple: '#6b4c6b',
      // Text colors
      text: '#e8dcc8',
      textDim: '#b8a890',
      textBright: '#f5eedd',
    },
    fonts: {
      display: "'Bangers', cursive",
      comic: "'Comic Neue', cursive",
      typewriter: "'Special Elite', cursive",
      marker: "'Permanent Marker', cursive",
      tech: "'Special Elite', cursive",
      body: "'Special Elite', cursive",
    },
    effects: {
      overlay: 'paper',
      vignette: true,
      grain: true,
      contrast: 0.95,
      saturate: 0.7,
    },
    imagePrompt: {
      style: 'vintage 1940s golden age comic book style, warm sepia tones, aged paper texture, classic comic illustration, muted colors with {accentColor} highlights, retro nostalgic aesthetic, hand-drawn quality',
      negativePrompt: 'modern, digital, neon, bright colors, noir',
      accentColors: {
        danger: 'burnt orange',
        mystery: 'deep teal',
        hope: 'warm gold',
        tension: 'olive green',
        power: 'burgundy'
      }
    }
  }
};

// Helper to get CSS variables from a theme
export const getThemeCSSVariables = (themeId) => {
  const theme = THEMES[themeId];
  if (!theme) return {};

  return {
    '--color-black': theme.colors.black,
    '--color-darker': theme.colors.darker,
    '--color-dark': theme.colors.dark,
    '--color-mid': theme.colors.mid,
    '--color-light': theme.colors.light,
    '--color-primary': theme.colors.primary,
    '--color-primary-dark': theme.colors.primaryDark,
    '--color-primary-glow': theme.colors.primaryGlow,
    '--color-secondary': theme.colors.secondary,
    '--color-secondary-light': theme.colors.secondaryLight,
    '--color-accent-gold': theme.colors.accentGold,
    '--color-accent-blue': theme.colors.accentBlue,
    '--color-accent-green': theme.colors.accentGreen,
    '--color-accent-purple': theme.colors.accentPurple,
    '--color-text': theme.colors.text,
    '--color-text-dim': theme.colors.textDim,
    '--color-text-bright': theme.colors.textBright,
    '--font-display': theme.fonts.display,
    '--font-comic': theme.fonts.comic,
    '--font-typewriter': theme.fonts.typewriter,
    '--font-marker': theme.fonts.marker,
    '--font-tech': theme.fonts.tech,
    '--font-body': theme.fonts.body,
  };
};

// Get image prompt style for a theme
export const getImagePromptStyle = (themeId, mood = 'mystery') => {
  const theme = THEMES[themeId];
  if (!theme) return THEMES.noir.imagePrompt.style;

  const accentColor = theme.imagePrompt.accentColors[mood] || theme.imagePrompt.accentColors.mystery;
  return theme.imagePrompt.style.replace('{accentColor}', accentColor);
};

// Get all theme IDs
export const getThemeIds = () => Object.keys(THEMES);

// Get theme by ID
export const getTheme = (themeId) => THEMES[themeId] || THEMES.noir;

export default THEMES;
